import type { Recommendation } from '@/lib/api/readiness';

/**
 * Recommendation → colour palette for the readiness radar + badge
 * (T-0087 / E08-F05). Tailwind-resolvable class names where the value
 * lives in the JSX; raw hex/HSL where it has to be passed into a non-
 * Tailwind consumer (Recharts' SVG stroke / fill props).
 *
 * Hue choice maps to PM's go / conditional / no-go semantics — green
 * for go, amber for conditional, red for no-go. The "pending" entry
 * is used when `recommendation` is null (completeness gate not yet
 * cleared) — we paint the radar a muted slate so the chart still
 * renders but doesn't telegraph a band the rollup hasn't committed to.
 *
 * Accessibility: the colour is paired with the recommendation text in
 * every place this palette is consumed; never colour-only meaning.
 */
export interface RecommendationPalette {
  /** Tailwind text + background class for the badge body. */
  badgeClass: string;
  /** Hex for Recharts <Radar fill> + <Radar stroke>. */
  chartHex: string;
  /** Hex for the rgba() fill — Recharts wants `fill` + `fillOpacity`. */
  chartFillOpacity: number;
  /** Display label. */
  label: string;
}

export const RECOMMENDATION_PALETTE: Record<Recommendation | 'pending', RecommendationPalette> = {
  go: {
    badgeClass: 'bg-emerald-600 text-white border-transparent',
    chartHex: '#059669', // emerald-600
    chartFillOpacity: 0.35,
    label: 'Go',
  },
  conditional_go: {
    badgeClass: 'bg-amber-500 text-white border-transparent',
    chartHex: '#d97706', // amber-600
    chartFillOpacity: 0.35,
    label: 'Conditional Go',
  },
  no_go: {
    badgeClass: 'bg-red-600 text-white border-transparent',
    chartHex: '#dc2626', // red-600
    chartFillOpacity: 0.35,
    label: 'No-Go',
  },
  pending: {
    badgeClass: 'bg-slate-500 text-white border-transparent',
    chartHex: '#64748b', // slate-500
    chartFillOpacity: 0.25,
    label: 'Pending',
  },
};

export function paletteFor(recommendation: Recommendation | null): RecommendationPalette {
  return RECOMMENDATION_PALETTE[recommendation ?? 'pending'];
}
